import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { environment } from './environments/environment';
import Gleap from 'gleap';
import { APPS_CREDENTIAL_CALLBACK_KEY } from './app/features/apps/constants/credential-callback.constants';

// Captura os parâmetros do callback de conexão ANTES do Angular inicializar. Com hash
// routing (`withHashLocation`), o Angular faz um `replaceState` na inicialização e
// descarta o `?code=...&state=...` que o provedor devolve ANTES do `#` - então
// guardamos aqui pra o componente de callback consumir depois. Dois shapes chegam no
// mesmo callback: OAuth (`code` + `state`) e instalação de app (`installation_id` +
// `setup_action` + `state`, ex: GitHub App).
(() => {
  try {
    const merged = new URLSearchParams();
    const sources = [window.location.search, window.location.hash.split('?')[1] ?? ''];
    for (const source of sources) {
      new URLSearchParams(source).forEach((value, key) => {
        if (!merged.has(key)) {
          merged.set(key, value);
        }
      });
    }
    if (merged.has('state') && (merged.has('code') || merged.has('installation_id'))) {
      sessionStorage.setItem(APPS_CREDENTIAL_CALLBACK_KEY, merged.toString());
    }
  } catch {
    // Ambiente sem sessionStorage/URL - ignora; o callback ainda tenta ler da URL viva.
  }
})();

if (environment.GLEAP_ENABLED && environment.GLEAP_API_KEY) {
  Gleap.setLanguage('pt-BR');
  Gleap.initialize(environment.GLEAP_API_KEY);

  // O launcher do Gleap nunca aparece por conta própria: quem chama o suporte é a
  // ação "Falar com o suporte" do gatilho da Iara (e o caminho de erro do
  // `ErrorService`), via `startConversation()`. Sem isto, dois botões flutuantes
  // disputam o mesmo canto - e o do Gleap tem `z-index: 2147483630`, então cobre
  // tudo.
  //
  // Fica no boot, e não no componente do gatilho, porque o gatilho não é montado
  // em toda rota (em `/chat` ele não existe) e porque esconder ao montar/devolver
  // ao destruir fazia o launcher do Gleap voltar a piscar na tela.
  //
  // Seguro antes de o botão existir: `injectFeedbackButton()` do SDK chama
  // `updateFeedbackButtonState()` ao criar o elemento, então o flag é honrado
  // quando ele entra no DOM. E `startConversation()` não consulta esse flag, então
  // a conversa abre normalmente com o launcher escondido.
  Gleap.showFeedbackButton(false);
}

Sentry.init({
  dsn: 'https://e96f6a22dcffc7604990d065faedf963@o142032.ingest.us.sentry.io/4509091861168129',
  enabled: environment.SENTRY_ENABLED,
  environment: environment.SENTRY_ENV,
  release:
    environment.GRAPHQL_CLIENT_VERSION === '<REPLACED_BY_CI_CD>'
      ? 'flux-client@1.0.0'
      : environment.GRAPHQL_CLIENT_VERSION,
  tracesSampleRate: 0.01,
  ignoreErrors: [
    // Persisted query errors
    /.*PersistedQueryNotFound.*/,
    /.*PERSISTED_QUERY_NOT_FOUND.*/,
    /You do not have the permission\(s\) .* to interact with this resource\./,

    /Fields required not answered/,
    /Card has tasks not completed/,

    /You must be signed in to view this resource\./,
    /Invariant Violation|An error occurred! For more details, see the full error text at https:\/\/go\.apollo\.dev\/.*/,
  ],
});

bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
